import React from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Linking,
  Platform} from "react-native";
import commonStyles from "../../styles/commonStyles";
import AntDesign from "@expo/vector-icons/AntDesign";
import Foundation from '@expo/vector-icons/Foundation';
import { ITaxDoc } from "../../types/ITaxDoc";
import FontAwesome from '@expo/vector-icons/FontAwesome';
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { Tooltip } from "react-native-paper";
import Toast from "react-native-toast-message";
import Constants from "expo-constants";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getUserIdToUse } from "../../utils/getUserIdToUse";
import * as FileSystem from "expo-file-system/legacy";
import FormattedText from "../../components/FormattedText";
import OverflowTooltipText from "../../components/OverflowTooltipText";
import * as Sharing from "expo-sharing";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../components/AppText";
type TaxDetailsProps = {
  route: {
    params: {
      tax: ITaxDoc;
    };
  };

  navigation: {
    goBack: () => void;
  };
};

const TaxDetails: React.FC<TaxDetailsProps> = ({ route, navigation }) => {
  const { tax } = route.params;

  const openLink = (url: string) => {
    console.log("the urls i ", url)
    Linking.openURL(url).catch((err) =>
      console.error("Failed to open URL:", err)
    );
  };
  const saveAndShare = async (fileUri: string, fileName: string) => {
    try {
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri, {
          mimeType: "application/pdf",
          dialogTitle: `Share ${fileName}`});
      } else {
        Toast.show({
          type: "info",
          text1: "File saved locally"});
      }
    } catch (error) {
      console.error("Error saving or sharing file:", error);
      Toast.show({
        type: "error",
        text1: "Error saving or sharing file."});
    }
  };

  const downloadFile = async (fileName: string) => {
    const bucketName = Constants.expoConfig?.extra?.BUCKET_NAME;
    const baseUrl = Constants.expoConfig?.extra?.API_URL;

    Toast.show({
      type: "success",
      text1: "Your download started!"});

   const url = `${baseUrl}portal/shareholding/download?fileName=${encodeURIComponent(
    fileName
  )}&bucketName=${encodeURIComponent(bucketName)}`;

    try {
      const token = await AsyncStorage.getItem("userToken");
      const userId = await getUserIdToUse();

      // `fileName` is a full storage key with folders (e.g. "A/B/C/file.pdf").
      // Use only the last segment for the local file — downloadAsync won't
      // create intermediate directories.
      const baseName = fileName.split("/").pop() || fileName;

      // On Android, let the user pick a destination folder up front so we don't
      // download anything if they cancel.
      let androidDirectoryUri: string | undefined;
      if (Platform.OS === "android") {
        const permissions =
          await FileSystem.StorageAccessFramework.requestDirectoryPermissionsAsync();
        if (!permissions.granted) {
          Toast.show({
            type: "error",
            text1: "Permission denied to access the directory."});
          return;
        }
        androidDirectoryUri = permissions.directoryUri;
      }

      // Single download for every platform — straight to a file, never a Blob
      // (RN can't build a Blob from an ArrayBuffer, which is why blob() threw).
      const tempUri = `${FileSystem.cacheDirectory}${baseName}`;
      const download = await FileSystem.downloadAsync(url, tempUri, {
        headers: {
          Authorization: `Bearer ${token}`,
          emulate_partner_id: userId?.toString() || ""}});

      if (download.status !== 200) {
        throw new Error(`Failed to download file. Status: ${download.status}`);
      }

      if (Platform.OS === "android") {
        // Copy the downloaded file into the user-chosen folder via SAF.
        const base64Data = await FileSystem.readAsStringAsync(download.uri, {
          encoding: FileSystem.EncodingType.Base64});
        const fileUri = await FileSystem.StorageAccessFramework.createFileAsync(
          androidDirectoryUri!,
          baseName,
          "application/pdf"
        );
        await FileSystem.writeAsStringAsync(fileUri, base64Data, {
          encoding: FileSystem.EncodingType.Base64});
        console.log("File successfully saved to:", fileUri);
      } else {
        await saveAndShare(download.uri, baseName);
      }

      Toast.show({
        type: "success",
        text1: "File downloaded successfully!"});
    } catch (error) {
      console.log('the error is ', error)
      Toast.show({
        type: "error",
        text1: "Download Failed. Unable to download the file."});
    }
  };


  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.flex1, commonStyles.screenBottomGap]}>
      <View style={commonStyles.container}>
        <TouchableOpacity
          testID="back-button"
          onPress={() => navigation.goBack()}
          style={commonStyles.subHeaderContainer}
        >
          <AntDesign name="left" size={24} color="black" />
          <OverflowTooltipText
            testID={`entity-name-${tax.id}`}
            text={tax.entityName}
            style={commonStyles.subHeader}
            onPress={() => navigation.goBack()}
          />
        </TouchableOpacity>
        <ScrollView testID="scroll-view">
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.mb10,
              commonStyles.p8,
              commonStyles.mt10,
              commonStyles.mlar2,
            ]}
          >
            <View style={commonStyles.columnContainer}>
              <AppText testID="partner-name" style={commonStyles.sectionLabel}>
                Partner Name
              </AppText>
              <View style={{ width: "100%", alignItems: "flex-end" }}>
                <OverflowTooltipText
                  testID="partner-name-value"
                  text={tax.shareholderName || "N/A"}
                  style={commonStyles.valueText}
                />
              </View>
            </View>

            <View style={commonStyles.subDetails}>
              <AppText testID="k1-document" style={commonStyles.subTitle}>
                K1 Document
              </AppText>
              <AppText style={commonStyles.link} onPress={() => openLink(tax.url)}>
                {tax.url ? (
                  <TouchableOpacity
                    testID="download-k1"
                    onPress={() => {
                      downloadFile(tax.url);
                    }}
                  >
                    <FontAwesome name="file-pdf-o" size={30} color="#cb180f"  style={{ marginTop: 3 }}  />
                  </TouchableOpacity>
                ) : (
                  <Tooltip
                    title="Not Available"
                    theme={{ colors: { background: "#fff", text: "#000" } }}
                  >
                    <AppText style={commonStyles.subValue}>N/A</AppText>
                  </Tooltip>
                )}
              </AppText>
            </View>

            <AppText
              style={[
                commonStyles.detailsHeader,
                commonStyles.mt15,
                commonStyles.ml10,
              ]}
              testID="k1-details"
            >
              K1 Details
            </AppText>
            <View style={commonStyles.columnContainer}>
              <AppText testID="ownership" style={commonStyles.sectionLabel}>
                J:Ownership
              </AppText>
              <FormattedText
                testID="ownership-value"
                value={tax.ownership}
                type="percentage"
                decimalPlaces={6}
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <AppText
                testID="beginning-liability"
                style={commonStyles.sectionLabel}
              >
                K:Beginning Liability
              </AppText>
              <FormattedText
                testID="beginning-liability-value"
                value={tax.beginningRecourse || tax.beginningNonrecourse}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>
            <View style={commonStyles.columnContainer}>
              <AppText testID="ending-liability" style={commonStyles.sectionLabel}>
                K:Ending Liability
              </AppText>
              <FormattedText
                testID="ending-liability-value"
                value={tax.endingRecourse || tax.endingNonrecourse}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="beginning-capital-account"
                style={commonStyles.sectionLabel}
                text="L:Beginning Capital Account"
              />
              <FormattedText
                testID="beginning-capital-account-value"
                value={tax.beginningCapitalAccount}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="capital-contribution"
                style={commonStyles.sectionLabel}
                text="L:Capital Contribution"
              />
              <FormattedText
                testID="capital-contribution-value"
                value={tax.capitalContributions}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="current-year-net-income"
                style={commonStyles.sectionLabel}
                text="L:Current Year Net Income"
              />
              <FormattedText
                testID="current-year-net-income-value"
                value={tax.currentYearNetIncome}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <AppText testID="other-increase" style={commonStyles.sectionLabel}>
                L:Other Increase
              </AppText>
              <FormattedText
                testID="other-increase-value"
                value={tax.otherIncreases}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="withdrawals/distribution"
                style={commonStyles.sectionLabel}
                text="L:Withdrawals/Distribution"
              />
              <FormattedText
                testID="withdrawals-distribution-value"
                value={tax.otherWithdrawals}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="ending-capital-account"
                style={commonStyles.sectionLabel}
                text="L:Ending Capital Account"
              />
              <FormattedText
                testID="ending-capital-account-value"
                value={tax.endingCapitalAccount}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="ordinary-business-account"
                style={commonStyles.sectionLabel}
                text="1:Ordinary Business Account"
              />
              <FormattedText
                testID="ordinary-business-account-value"
                value={tax.ordinaryBusinessIncome}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="net-rental-real-estate-income"
                style={commonStyles.sectionLabel}
                text="2:Net Rental Real Estate Income"
              />
              <FormattedText
                testID="net-rental-real-estate-income-value"
                value={tax.netRentalRealEstateIncome}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <AppText testID="interestIncome" style={commonStyles.sectionLabel}>
                Interest Income
              </AppText>
              <FormattedText
                testID="interest-income-value"
                value={tax.interestIncome}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <AppText
                testID="ordinary-dividend"
                style={commonStyles.sectionLabel}
              >
                64:Ordinary Dividend
              </AppText>
              <FormattedText
                testID="ordinary-dividend-value"
                value={tax.ordinaryDividends}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="net-short-term-capital-gain"
                style={commonStyles.sectionLabel}
                text="8: Net Short-Term Capital Gain"
              />
              <FormattedText
                testID="net-short-term-capital-gain-value"
                value={tax.netShortTermCapitalGain}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="net-long-term-capital-gain"
                style={commonStyles.sectionLabel}
                text="9A: Net Long-Term Capital Gain"
              />
              <FormattedText
                testID="net-long-term-capital-gain-value"
                value={tax.netLongTermCapitalGain}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="net-section-gain"
                style={commonStyles.sectionLabel}
                text="10:Net Section 1231 gain (loss)"
              />
              <FormattedText
                testID="net-section-gain-value"
                value={tax.netSection1231Gain}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <AppText testID="other-deductions" style={commonStyles.sectionLabel}>
                13H:Other Deductions
              </AppText>
              <FormattedText
                testID="other-deductions-value"
                value={tax.otherDeductions}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="self-employment"
                style={commonStyles.sectionLabel}
                text="14A:Self Employment Earning/Loss"
              />
              <FormattedText
                testID="self-employment-value"
                value={tax.selfEmploymentEarnings}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <AppText testID="credits" style={commonStyles.sectionLabel}>
                15:Credits
              </AppText>
              <FormattedText
                testID="credits-value"
                value={tax.credits}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="alternative-minimum-tax"
                style={commonStyles.sectionLabel}
                text="17:Alternative minimum tax (AMT) items"
              />
              <FormattedText
                testID="alternative-minimum-tax-value"
                value={tax.alternativeMinimumTaxItems}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="tax-exempt-income-and-expenses"
                style={commonStyles.sectionLabel}
                text="18:Tax Exempt Income and Expenses"
              />
              <FormattedText
                testID="tax-exempt-income-and-expenses-value"
                value={tax.taxExemptIncomeAndNondeductibleExpenses}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>

            <View style={commonStyles.columnContainer}>
              <OverflowTooltipText
                testID="other-information"
                style={commonStyles.sectionLabel}
                text="20A: Other information"
              />
              <FormattedText
                testID="other-information-value"
                value={tax.otherInformation}
                type="currency"
                style={commonStyles.valueText}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default TaxDetails;
